import {
  Box,
  InputAdornment,
  Paper,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Button,
  Typography,
  useTheme,
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import React from "react";
import CommonTablePagination from "../../components/CommonTablePagination/CommonTablePagination";
import { format } from "date-fns";
const safeStr = (v) => (typeof v === "string" ? v : v == null ? "" : String(v));

const getResumeNameFromUrl = (url) => {
  const raw = safeStr(url).trim();
  if (!raw) return "";
  try {
    const pathname = new URL(raw).pathname || "";
    const last = decodeURIComponent(pathname.split("/").filter(Boolean).pop() || "");
    if (!last) return "";
    return last.replace(/\.[^.]+$/, "");
  } catch {
    const last = decodeURIComponent(raw.split("/").filter(Boolean).pop() || "");
    if (!last) return "";
    return last.replace(/\?.*$/, "").replace(/\.[^.]+$/, "");
  }
};

const parseDateToMs = (value) => {
  if (!value) return null;
  if (value instanceof Date) {
    const t = value.getTime();
    return Number.isFinite(t) ? t : null;
  }
  const s = safeStr(value).trim();
  if (!s) return null;
  const t = new Date(s).getTime();
  return Number.isFinite(t) ? t : null;
};

const CandidateProfileTable = ({ rows, loading, onViewDetails }) => {
  const theme = useTheme();
  const list = Array.isArray(rows) ? rows : [];

  const [nameQuery, setNameQuery] = React.useState("");
  const [statusFilter, setStatusFilter] = React.useState("");
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);
  const [dateSortDirection, setDateSortDirection] = React.useState("desc");

  const filtered = list.filter((r) => {
    const q = nameQuery.trim().toLowerCase();
    // Keep filtering logic aligned with how we render the cells (fallback keys).
    const name = safeStr(r?.candidateName ?? r?.fullName).toLowerCase();
    const email = safeStr(r?.candidateEmail ?? r?.email).toLowerCase();
    const matchesQuery = q ? name.includes(q) || email.includes(q) : true;
    const matchesStatus = statusFilter ? (r?.status ?? "") === statusFilter : true;
    return matchesQuery && matchesStatus;
  });

  // To sort the candidates by applied date
  const sorted = React.useMemo(() => {
    const dir = dateSortDirection === "asc" ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const aMs = parseDateToMs(a?.createdAt);
      const bMs = parseDateToMs(b?.createdAt);
      if (aMs == null && bMs == null) return 0;
      if (aMs == null) return 1; // keep unknown dates at bottom
      if (bMs == null) return -1;
      return (aMs - bMs) * dir;
    });
  }, [filtered, dateSortDirection]);

  // To paginate the candidates
  const paginated = sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // To reset the page when the name query or status filter changes
  React.useEffect(() => {
    setPage(0);
  }, [nameQuery, statusFilter]);

  // Show newly added candidates from the first page after a refresh
  React.useEffect(() => {
    setPage(0);
  }, [list.length]);

  // Method to download the resume
  const handleDownloadResume = async (row) => {
    const url = row?.resumeUrl ?? row?.resumeURL ?? row?.resume ;
    const u = safeStr(url).trim();
    if (!u) return;
    window.open(u, "_blank", "noopener,noreferrer");
  };

  const handleViewDetails = (row) => {
    onViewDetails?.(row);
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 3,
          overflow: "hidden",
          backgroundColor: theme.palette.background.paper,
        }}
      >
        {/* HEADER (match dashboard details table) */}
        <Box
          sx={{
            p: 2,
            borderBottom: `1px solid ${theme.palette.divider}`,
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "stretch", sm: "center" },
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            All Candidates
          </Typography>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              width: { xs: "100%", sm: "auto" },
            }}
          >
            <TextField
              size="small"
              placeholder="Search by name or email"
              value={nameQuery}
              onChange={(e) => {
                setNameQuery(e.target.value);
                setPage(0);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon sx={{ fontSize: 18, color: theme.palette.text.secondary }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: { xs: "100%", sm: "auto" },
                minWidth: { xs: 0, sm: 240 },
                flex: { xs: 1, sm: "0 1 auto" },
                "& .MuiOutlinedInput-root": { borderRadius: 1.5 },
              }}
            />

            {/* <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel id="candidate-status-filter-label" shrink>
                Status
              </InputLabel>
              <Select
                labelId="candidate-status-filter-label"
                id="candidate-status-filter"
                value={statusFilter}
                label="Status"
                size="small"
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(0);
                }}
                displayEmpty
                renderValue={(v) => (v ? v : "All")}
                sx={{
                  backgroundColor: theme.palette.background.paper,
                  borderRadius: 1.5,
                  fontSize: "0.875rem",
                }}
              >
                <MenuItem value="" sx={{ fontSize: "0.875rem" }}>
                  All
                </MenuItem>
                <MenuItem value="Scheduled" sx={{ fontSize: "0.875rem" }}>
                  Scheduled
                </MenuItem>
                <MenuItem value="InProgress" sx={{ fontSize: "0.875rem" }}>
                  Processing
                </MenuItem>
                <MenuItem value="Completed" sx={{ fontSize: "0.875rem" }}>
                  Completed
                </MenuItem>
              </Select>
            </FormControl> */}
          </Box>
        </Box>

      <TableContainer sx={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
        <Table sx={{ minWidth: { xs: 900, md: 960 } }}>
          <TableHead>
            <TableRow>
              {[
                { label: "Candidate Name" },
                { label: "Email" },
                { label: "Contact Number" },
                { label: "Experience" },
                { label: "Job Title" },
                { label: "Resume" },
                // { label: "Source" },
                // { label: "Screening" },
                { label: "Applied Date", sortKey: "appliedDate" },
                { label: "Actions" },
              ].map((col) => (
                <TableCell
                  key={col.label}
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.text.secondary,
                    fontSize: "0.875rem",
                  }}
                >
                  {col.sortKey === "appliedDate" ? (
                    <TableSortLabel
                      sx={{
                        cursor: "pointer",
                        color: theme.palette.text.secondary,
                        "&.Mui-active": { color: theme.palette.text.secondary },
                        "& .MuiTableSortLabel-icon": {
                          color: `${theme.palette.text.secondary} !important`,
                        },
                      }}
                      active
                      direction={dateSortDirection}
                      onClick={() =>
                        setDateSortDirection((prev) => (prev === "asc" ? "desc" : "asc"))
                      }
                    >
                      {col.label}
                    </TableSortLabel>
                  ) : (
                    col.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={10}>
                  <Typography color="text.secondary" fontWeight={600} sx={{ py: 2 }}>
                    Loading candidates…
                  </Typography>
                </TableCell>
              </TableRow>
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10}>
                  <Typography color="text.secondary" fontWeight={600} sx={{ py: 2 }}>
                    No candidates match the selected status.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginated.map((row, idx) => (
                <TableRow key={`${row?.id ?? ""}-${idx}`} hover>
                  <TableCell sx={{ fontWeight: 500, textTransform: "capitalize" }}>
                    {row?.candidateName ?? row?.fullName ?? "-"}
                  </TableCell>
                  <TableCell>{row?.candidateEmail ?? row?.email ?? "-"}</TableCell>
                  <TableCell>{row?.phone ?? "-"}</TableCell>
                  <TableCell>
                    {row.experience || "-"   }
                  </TableCell>
                  <TableCell>{row?.position ?? "-"}</TableCell>
                  <TableCell>
                    {!!(row?.resumeUrl ?? row?.resumeURL ?? row?.resume ?? row?.resume_link) ? (
                      <Box sx={{ display: "inline-flex", alignItems: "center", maxWidth: 140, gap: 0.5 }}>
                        <IconButton
                          size="small"
                          color="primary"
                          aria-label="Download resume"
                          onClick={() => handleDownloadResume(row)}
                          sx={{
                            border: `1px solid ${theme.palette.divider}`,
                            bgcolor: theme.palette.background.paper,
                            "&:hover": { bgcolor: "rgba(148,163,184,0.12)" },
                          }}
                        >
                          <DownloadOutlinedIcon fontSize="small" />
                        </IconButton>
                        {(() => {
                          const resumeUrl =
                            row?.resumeUrl ?? row?.resumeURL ?? row?.resume ?? row?.resume_link ?? "";
                          const fullName = getResumeNameFromUrl(resumeUrl);
                          if (!fullName) return null;
                          return (
                            <Tooltip title={fullName} placement="top">
                              <Typography
                                variant="caption"
                                sx={{
                                  maxWidth: 95,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  display: "inline-block",
                                  verticalAlign: "middle",
                                }}
                              >
                                {fullName}
                              </Typography>
                            </Tooltip>
                          );
                        })()}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        -
                      </Typography>
                    )}
                  </TableCell>
                  {/* <TableCell>
                    <Chip size="small" label={row?.source ?? "-"} sx={{ fontWeight: 650 }} />
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={row?.status ?? "InProgress"} />
                  </TableCell> */}
                  <TableCell>{row?.createdAt ? format(new Date(row?.createdAt), "MM/dd/yyyy") : "-"}</TableCell>
                  <TableCell>
                    <Button
                      variant="outlined"
                      size="small"
                      color="primary"
                      onClick={() => handleViewDetails(row)}
                      sx={{ textTransform: "none", whiteSpace: "nowrap" }}
                    >
                      View Profile
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

        {/* PAGINATION (match dashboard behavior) */}
        {filtered.length > 0 && (
          <CommonTablePagination
            count={filtered.length}
            page={page}
            onPageChange={(_, nextPage) => setPage(nextPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25]}
          />
        )}
      </Paper>
    </Box>
  );
};

export default CandidateProfileTable;

